'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppNav from '@/components/AppNav';
import { PageErrorBoundary } from '@/components/ErrorBoundary';
import { useToast } from '@/components/Toast';

interface GeneratedDescription {
  niceClass: number;
  classTitle: string;
  description: string;
  keywords: string[];
  wordCount: number;
}

const NICE_CLASSES = [
  { class: 1, title: 'Chemicals' },
  { class: 2, title: 'Paints & Coatings' },
  { class: 3, title: 'Cosmetics & Cleaning' },
  { class: 4, title: 'Lubricants & Fuels' },
  { class: 5, title: 'Pharmaceuticals' },
  { class: 9, title: 'Electronics & Software' },
  { class: 16, title: 'Paper & Printed Matter' },
  { class: 25, title: 'Clothing & Footwear' },
  { class: 29, title: 'Food Products' },
  { class: 30, title: 'Staple Foods' },
  { class: 35, title: 'Advertising & Business' },
  { class: 36, title: 'Finance & Insurance' },
  { class: 38, title: 'Telecommunications' },
  { class: 41, title: 'Education & Entertainment' },
  { class: 42, title: 'Technology Services' },
  { class: 43, title: 'Food & Drink Services' },
  { class: 44, title: 'Medical & Beauty Services' },
  { class: 45, title: 'Legal & Security Services' },
];

const TONES = [
  { value: 'formal', label: 'Formal / Legal', desc: 'Precise legal language for trademark filing' },
  { value: 'comprehensive', label: 'Comprehensive', desc: 'Broad coverage with all related goods/services' },
  { value: 'specific', label: 'Specific / Narrow', desc: 'Targeted description for core products only' },
];

const demoDescriptions: Record<number, string> = {
  3: 'Perfumes; eau de cologne; essential oils for personal use; cosmetics; skincare preparations; face creams; body lotions; hair care preparations; shampoos; conditioners; soaps; bath preparations; deodorants for personal use; all included in Class 3.',
  35: 'Retail services for perfumes, cosmetics and beauty products; online retail services for personal care products; wholesale services for fragrances and cosmetics; advertising services for beauty brands; business management services for cosmetics companies; all included in Class 35.',
  44: 'Beauty salon services; spa services; aromatherapy services; skin care treatment services; cosmetic treatment services; all included in Class 44.',
};

export default function GoodsServicesWriterPage() {
  const [businessDescription, setBusinessDescription] = useState('');
  const [selectedClasses, setSelectedClasses] = useState<number[]>([3]);
  const [tone, setTone] = useState('formal');
  const [generating, setGenerating] = useState(false);
  const [results, setResults] = useState<GeneratedDescription[]>([]);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const { success, error: toastError, info } = useToast();

  const toggleClass = (cls: number) => {
    setSelectedClasses(prev =>
      prev.includes(cls) ? prev.filter(c => c !== cls) : [...prev, cls].slice(0, 10)
    );
  };

  const generate = async () => {
    if (!businessDescription.trim()) {
      toastError('Description required', 'Please describe your business or products first.');
      return;
    }
    if (selectedClasses.length === 0) {
      toastError('Select classes', 'Please select at least one Nice class.');
      return;
    }
    setGenerating(true);
    setResults([]);

    try {
      const res = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'chat',
          messages: [{
            role: 'user',
            content: `You are a trademark attorney specializing in Indian trademark law. Generate professional trademark goods and services descriptions for the following business:

Business: ${businessDescription}
Nice Classes: ${selectedClasses.join(', ')}
Tone: ${tone}

For EACH class, provide:
1. A precise, legally-appropriate description suitable for Indian trademark filing
2. 5-7 relevant keywords
3. The description should follow IP India's accepted format

Format your response as JSON array:
[{"niceClass": 3, "classTitle": "...", "description": "...", "keywords": ["...", "..."]}]

Only include the JSON array, no other text.`,
          }],
        }),
      });
      const data = await res.json();

      try {
        const parsed = JSON.parse(data.response || '[]');
        const withWordCount = parsed.map((d: any) => ({
          ...d,
          wordCount: d.description?.split(/\s+/).length || 0,
        }));
        setResults(withWordCount);
        success('Descriptions generated', `${withWordCount.length} class description(s) ready.`);
      } catch {
        // Fallback to demo data
        const fallback = selectedClasses.map(cls => {
          const classInfo = NICE_CLASSES.find(c => c.class === cls);
          return {
            niceClass: cls,
            classTitle: classInfo?.title || `Class ${cls}`,
            description: demoDescriptions[cls] || `Goods and services related to ${businessDescription} in Class ${cls}; all included in Class ${cls}.`,
            keywords: ['perfumes', 'cosmetics', 'skincare', 'fragrances', 'beauty products'].slice(0, 5),
            wordCount: 30,
          };
        });
        setResults(fallback);
        info('Demo mode', 'Add OPENROUTER_API_KEY for AI-generated descriptions.');
      }
    } catch {
      toastError('Generation failed', 'Please try again or check your API key.');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = async (text: string, cls: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(cls);
      success('Copied!', 'Description copied to clipboard.');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toastError('Copy failed', 'Please select and copy the text manually.');
    }
  };

  const downloadAll = () => {
    const content = results.map(r =>
      `CLASS ${r.niceClass} — ${r.classTitle}\n${'─'.repeat(50)}\n${r.description}\n\nKeywords: ${r.keywords.join(', ')}\n`
    ).join('\n\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'trademark-goods-services.txt';
    a.click();
    URL.revokeObjectURL(url);
    success('Downloaded!', 'Descriptions saved as text file.');
  };

  return (
    <PageErrorBoundary>
      <div className="min-h-screen bg-surface-2 font-sans">
        <AppNav variant="app" activePath="/trademark/writer" />

        <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen">
          <div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8">

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Link href="/trademark" className="text-[12px] text-ink-muted hover:text-ink transition-colors">Trademark</Link>
                  <span className="text-ink-subtle">›</span>
                  <span className="text-[12px] text-ink font-medium">AI Goods & Services Writer</span>
                </div>
                <h1 className="text-2xl font-bold text-ink">AI Goods & Services Writer</h1>
                <p className="text-[14px] text-ink-muted mt-0.5">Generate professional trademark descriptions for IP India filing.</p>
              </div>
              {results.length > 0 && (
                <button
                  onClick={downloadAll}
                  className="flex items-center gap-2 bg-white border border-border text-[13px] font-medium text-ink-muted px-4 py-2.5 rounded-xl hover:border-ai/30 hover:text-ink transition-all"
                >
                  ⬇ Download All
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              {/* Left: Input */}
              <div className="lg:col-span-2 space-y-5">
                <div className="bg-white rounded-xl border border-border p-5">
                  <h3 className="text-[13px] font-semibold text-ink mb-4">1. Describe Your Business</h3>
                  <textarea
                    value={businessDescription}
                    onChange={e => setBusinessDescription(e.target.value)}
                    placeholder="e.g. We manufacture and sell luxury perfumes, essential oils, and skincare products under the brand ELYSIUM. We also operate beauty salons and offer aromatherapy services."
                    rows={5}
                    className="w-full bg-surface-2 border border-border rounded-xl px-4 py-3 text-[13px] text-ink placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai/40 resize-none leading-relaxed"
                  />
                  <p className="text-[11px] text-ink-subtle mt-2">{businessDescription.length} characters</p>
                </div>

                <div className="bg-white rounded-xl border border-border p-5">
                  <h3 className="text-[13px] font-semibold text-ink mb-3">2. Select Nice Classes</h3>
                  <p className="text-[11px] text-ink-muted mb-3">Select up to 10 classes. Each class = separate filing fee.</p>
                  <div className="grid grid-cols-2 gap-1.5 max-h-64 overflow-y-auto">
                    {NICE_CLASSES.map(c => (
                      <button
                        key={c.class}
                        onClick={() => toggleClass(c.class)}
                        className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-left transition-all text-[11px] ${
                          selectedClasses.includes(c.class)
                            ? 'bg-ai-light border border-ai/30 text-ai' :'bg-surface-2 border border-transparent text-ink-muted hover:text-ink hover:bg-surface-3'
                        }`}
                      >
                        <span className="font-bold flex-shrink-0">{c.class}</span>
                        <span className="truncate">{c.title}</span>
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-ink-muted mt-2">{selectedClasses.length} class(es) selected</p>
                </div>

                <div className="bg-white rounded-xl border border-border p-5">
                  <h3 className="text-[13px] font-semibold text-ink mb-3">3. Description Tone</h3>
                  <div className="space-y-2">
                    {TONES.map(t => (
                      <label key={t.value} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${tone === t.value ? 'border-ai/30 bg-ai-light' : 'border-border hover:border-ai/20'}`}>
                        <input type="radio" name="tone" value={t.value} checked={tone === t.value} onChange={() => setTone(t.value)} className="mt-0.5" />
                        <div>
                          <p className="text-[12px] font-semibold text-ink">{t.label}</p>
                          <p className="text-[11px] text-ink-muted">{t.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <button
                  onClick={generate}
                  disabled={generating || !businessDescription.trim() || selectedClasses.length === 0}
                  className="w-full bg-ai text-white font-semibold py-3.5 rounded-xl hover:bg-ai-dark transition-all disabled:opacity-50 text-[14px]"
                >
                  {generating ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Generating descriptions...
                    </span>
                  ) : '✨ Generate Descriptions →'}
                </button>
              </div>

              {/* Right: Results */}
              <div className="lg:col-span-3">
                {results.length === 0 ? (
                  <div className="bg-white rounded-xl border border-border p-10 text-center h-full flex flex-col items-center justify-center">
                    <div className="w-16 h-16 bg-ai-light rounded-2xl flex items-center justify-center mb-4">
                      <span className="text-3xl">✍️</span>
                    </div>
                    <h3 className="text-[15px] font-semibold text-ink mb-2">Ready to generate</h3>
                    <p className="text-[13px] text-ink-muted max-w-xs">Describe your business, select Nice classes, and click Generate to get professional trademark descriptions.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {results.map(result => (
                      <div key={result.niceClass} className="bg-white rounded-xl border border-border p-5">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="bg-ai text-white text-[11px] font-bold px-2.5 py-1 rounded-lg">Class {result.niceClass}</span>
                            <span className="text-[13px] font-semibold text-ink">{result.classTitle}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] text-ink-muted">{result.wordCount} words</span>
                            <button
                              onClick={() => copyToClipboard(result.description, result.niceClass)}
                              className={`text-[11px] font-semibold px-3 py-1.5 rounded-lg transition-all ${
                                copiedId === result.niceClass
                                  ? 'bg-success-light text-success' :'bg-surface-2 text-ink-muted hover:text-ink hover:bg-surface-3'
                              }`}
                            >
                              {copiedId === result.niceClass ? '✓ Copied' : '⎘ Copy'}
                            </button>
                          </div>
                        </div>
                        <div className="bg-surface-2 rounded-xl p-4 mb-3">
                          <p className="text-[13px] text-ink leading-relaxed">{result.description}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold text-ink-muted mb-2">Keywords</p>
                          <div className="flex flex-wrap gap-1.5">
                            {result.keywords.map(kw => (
                              <span key={kw} className="text-[11px] bg-surface-3 text-ink-muted px-2 py-0.5 rounded-full">{kw}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="bg-warning/8 border border-warning/20 rounded-xl p-4">
                      <p className="text-[12px] font-semibold text-warning mb-1">⚠ Professional Review Required</p>
                      <p className="text-[12px] text-ink-muted">AI-generated descriptions must be reviewed by a qualified trademark attorney before filing with IP India. Incorrect descriptions can lead to refusal or limited protection.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </PageErrorBoundary>
  );
}
